import {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import {formatDate} from '@/utils';
import {useLoadMore} from '@/common-pages/hooks/load-more.hooks';
import {
  getTeamReportDataList,
  getAgentInfo,
} from '@/common-pages/proxy/proxy.service';
import {AgentInfo} from '@/common-pages/proxy/proxy.type';
import {getAgentInfoExtra} from '../agent.service';
import {AgentInfoExtra} from '../agent.type';
import {createTableCells} from './components/team-table-cells';
import {useUserViewModal} from './hooks/use-user-view-modal';

export function useAgentDetail() {
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [showMinCharsWarning, setShowMinCharsWarning] = useState(false);
  const [tier, setTier] = useState('1');
  const [tableSort, setTableSort] = useState<Record<string, any>>({});
  const [tableStartDate, setTableStartDate] = useState<number>(Date.now());
  const [tableEndDate, setTableEndDate] = useState<number>(Date.now());
  const [agentInfo, setAgentInfo] = useState<AgentInfo>();
  const [agentInfoExtra, setAgentInfoExtra] = useState<AgentInfoExtra>();
  const tableHeaderScrollRef = useRef<any>(null);
  const tableBodyScrollRef = useRef<any>(null);

  const handleTableHeaderScroll = (left: number) => {
    tableBodyScrollRef.current?.scrollTo(left);
  };

  const handleTableBodyScroll = (left: number) => {
    tableHeaderScrollRef.current?.scrollTo(left);
  };

  const {
    list,
    loadMore,
    hasMore,
    loading,
    reload: initList,
  } = useLoadMore((pageNum, pageSize) => {
    // 如果搜索词是1-2位，不调用接口，返回空数据
    if (searchValue.length > 0 && searchValue.length < 3) {
      return Promise.resolve({
        list: [],
        total: 0,
      });
    }

    const tsort: Record<string, number> = {};
    Object.keys(tableSort).map(key => {
      const map: Record<string, number> = {
        none: 0,
        asc: 1,
        desc: 2,
      };
      tsort[key] = map[tableSort[key]] || 0;
    });
    const startStr = tableStartDate
      ? formatDate(tableStartDate, 'yyyyMMdd')
      : null;
    const endStr = tableEndDate ? formatDate(tableEndDate, 'yyyyMMdd') : null;
    return getTeamReportDataList({
      pageNo: pageNum,
      pageSize,
      ...tsort,
      searchWord: searching && searchValue ? searchValue : undefined,
      leve: Number(tier),
      startDate: startStr,
      endDate: endStr,
    });
  }, 20);

  useEffect(() => {
    initList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSort, tableStartDate, tableEndDate, searchValue, tier]);

  // 获取代理信息
  useEffect(() => {
    Promise.all([
      getAgentInfo()
        .then(info => setAgentInfo(info))
        .catch(error => console.error('Failed to get agent info:', error)),
      getAgentInfoExtra()
        .then(extra => setAgentInfoExtra(extra))
        .catch(error =>
          console.error('Failed to get agent extra info:', error),
        ),
    ]);
  }, []);

  const {renderModal, show} = useUserViewModal();

  const bodyCell = useMemo(() => {
    return createTableCells(show);
  }, [show]);

  const handleSearch = useCallback(() => {
    setSearching(true);
    setShowMinCharsWarning(false);
  }, []);

  const handleAutoSearch = useCallback(() => {
    if (searchValue.length === 0) {
      // 空值：查询所有
      setSearching(true);
      setShowMinCharsWarning(false);
    } else if (searchValue.length >= 3) {
      // ≥3字符：执行查询
      setSearching(true);
      setShowMinCharsWarning(false);
    } else {
      // 1-2字符：不查询，显示提示
      setSearching(false);
      setShowMinCharsWarning(true);
    }
  }, [searchValue]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    if (value.length === 0) {
      setShowMinCharsWarning(false);
    } else if (value.length < 3) {
      setShowMinCharsWarning(true);
    } else {
      setShowMinCharsWarning(false);
    }
  }, []);

  const handleDateRangeChange = useCallback(
    (startDate?: number, endDate?: number) => {
      setTableStartDate(startDate || Date.now());
      setTableEndDate(endDate || Date.now());
    },
    [],
  );

  const handleTierChange = useCallback((newTier: string) => {
    setTier(newTier);
  }, []);

  const handleSortChange = useCallback((sortMap: Record<string, any>) => {
    setTableSort(sortMap);
  }, []);

  return {
    // 状态
    searchValue,
    searching,
    showMinCharsWarning,
    tier,
    tableSort,
    tableStartDate,
    tableEndDate,
    agentInfo,
    agentInfoExtra,
    list,
    loading,
    hasMore,

    // 引用
    tableHeaderScrollRef,
    tableBodyScrollRef,

    // 处理函数
    handleTableHeaderScroll,
    handleTableBodyScroll,
    handleSearch,
    handleAutoSearch,
    handleSearchChange,
    handleDateRangeChange,
    handleTierChange,
    handleSortChange,
    loadMore,

    // 计算值
    bodyCell,

    // 模态框
    renderModal,
  };
}
